import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtQty, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, WarehouseSelect } from "@/components/stock/FormField";
import { selectCls } from "@/components/stock/LineItemsEditor";
import { toast } from "sonner";

const ReceiveLines = ({ po, items, qtys, setQtys }) => {
  const { t } = useLang();
  const lookup = (type, id) => (items[type] || []).find((x) => x.id === id) || {};
  return (
    <table className="min-w-full text-sm border border-slate-200" data-testid="po-receive-lines">
      <thead className="bg-slate-100/80 text-slate-700">
        <tr>
          <th className="text-left py-2 px-3">{t("item")}</th>
          <th className="text-right py-2 px-3">{t("qty")} PO</th>
          <th className="text-right py-2 px-3">{t("received")}</th>
          <th className="text-right py-2 px-3">{t("remaining")}</th>
          <th className="text-right py-2 px-3 w-32">{t("receive_qty")}</th>
        </tr>
      </thead>
      <tbody>
        {po.lines.map((l, i) => {
          const item = lookup(l.item_type, l.item_id);
          const remain = l.qty - l.received_qty;
          return (
            <tr key={l.line_id} className="border-t border-slate-100">
              <td className="py-2 px-3"><div className="font-mono text-xs text-slate-500">{item.code}</div>{item.name}</td>
              <td className="py-2 px-3 text-right font-mono">{fmtQty(l.qty)}</td>
              <td className="py-2 px-3 text-right font-mono text-emerald-700">{fmtQty(l.received_qty)}</td>
              <td className="py-2 px-3 text-right font-mono text-amber-700">{fmtQty(remain)}</td>
              <td className="py-2 px-3">
                <Input type="number" min="0" className="h-9 text-right" disabled={remain <= 0} value={qtys[l.line_id] ?? 0}
                       onChange={(e) => setQtys({ ...qtys, [l.line_id]: e.target.value })} data-testid={`po-receive-qty-${i}`} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Goods receipt that always references an issued PO (keeps PO ↔ receipt traceable).
export const POReceiptForm = ({ items, fixedPo = null, onDone, onCancel }) => {
  const { t } = useLang();
  const [pos, setPos] = useState([]);
  const [poId, setPoId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [warehouse, setWarehouse] = useState("central");
  const [qtys, setQtys] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!fixedPo) api.get("/pos").then((r) => setPos(r.data.filter((p) => ["open", "partial"].includes(p.status))));
  }, [fixedPo]);
  const po = fixedPo || pos.find((p) => p.id === poId);
  useEffect(() => {
    if (po) setQtys(Object.fromEntries(po.lines.map((l) => [l.line_id, Math.max(0, l.qty - l.received_qty)])));
  }, [po]);

  const save = async () => {
    if (!po) return toast.error(t("select_po"));
    const lines = po.lines.map((l) => ({ line_id: l.line_id, qty: Number(qtys[l.line_id] || 0) })).filter((l) => l.qty > 0);
    setBusy(true);
    try {
      await api.post(`/pos/${po.id}/receive`, { date, warehouse, lines });
      toast.success(`${t("saved")} · ${po.po_number}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {!fixedPo && (
          <FormField label={t("select_po")}>
            <select className={selectCls} value={poId} onChange={(e) => setPoId(e.target.value)} data-testid="po-ref-select">
              <option value="">—</option>
              {pos.map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.vendor || "-"} · {p.status}</option>)}
            </select>
          </FormField>
        )}
        <FormField label={t("date")}>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="po-receive-date-input" />
        </FormField>
        <FormField label={t("warehouse")}>
          <WarehouseSelect value={warehouse} onChange={setWarehouse} availableOnly testid="po-receive-warehouse" />
        </FormField>
      </div>
      {po ? (
        <>
          <div className="text-xs text-slate-500">
            {t("vendor")}: <span className="font-medium text-slate-700">{po.vendor || "-"}</span> · {t("expected_date")}: <span className="font-mono">{(po.expected_date || "-").slice(0, 10)}</span>
          </div>
          <ReceiveLines po={po} items={items} qtys={qtys} setQtys={setQtys} />
        </>
      ) : (
        <div className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-md py-8 text-center">{t("select_po_hint")}</div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>{t("cancel")}</Button>
        <Button onClick={save} disabled={busy || !po} className="bg-emerald-700 hover:bg-emerald-800" data-testid="po-receive-save-btn">{t("save")}</Button>
      </div>
    </div>
  );
};
